import React from 'react';
import api from '../../../services/api';

/** Authenticated blob URL for a management-company logo (for printable reports). */
export function useManagementCompanyLogoBlobUrl(
  companyId: number | null | undefined,
  logoStorageKey: string | null | undefined,
) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const blobUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!companyId || !logoStorageKey) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/management-companies/${companyId}/logo`, {
        responseType: 'blob',
        headers: { Accept: 'image/*,application/octet-stream' },
      })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const blob = res.data as Blob;
        if (res.status === 204 || !blob || blob.size === 0) {
          setBlobUrl(null);
          return;
        }
        const type = (blob.type || '').toLowerCase();
        if (type.includes('json') || type.includes('text/')) {
          setBlobUrl(null);
          return;
        }
        const imageBlob =
          type.startsWith('image/') || type === 'application/octet-stream' || !type
            ? new Blob([blob], { type: type.startsWith('image/') ? type : 'image/png' })
            : blob;
        const url = URL.createObjectURL(imageBlob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [companyId, logoStorageKey]);

  return { logoBlobUrl: blobUrl };
}
